import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Input from '../components/input/Input';
import ImageUploader from '../components/imageUploader';
import Spinner from '../components/Spinner/spinner';
import { getSgrData, updateSgrData, SgrData } from '../services/SgrService';
import { getRiskMetrics } from '../services/GuaranteesService';
import { routes } from '../routes/routePaths';
import { Toast, useToast } from '../components/toast/toast';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import { cuitMask } from '../utils/Masks';
import { validateCuit } from '../utils/validationUtils';

interface RiskFundMetrics {
  nominal_fund: number | null;
  integrated_fund: number | null;
  active_contingent: number | null;
  available_fund: number | null;
  outstanding_risk: number | null;
  utilization_degree: number | null;
  leverage: number | null;
  maximum_guarantee: number | null;
  outstanding_risk_limit: number | null;
  pending_integration_fund: number | null;
  available_new_contributions: number | null;
}

const RISK_FUND_ROWS: { key: keyof RiskFundMetrics; label: string }[] = [
  { key: 'nominal_fund', label: 'Fondo Nominal' },
  { key: 'integrated_fund', label: 'Fondo Integrado' },
  { key: 'active_contingent', label: 'Contingente' },
  { key: 'available_fund', label: 'Fondo Disponible' },
  { key: 'outstanding_risk', label: 'Riesgo Vivo' },
  { key: 'utilization_degree', label: 'Grado de Utilización' },
  { key: 'leverage', label: 'Apalancamiento' },
  { key: 'maximum_guarantee', label: 'Aval Máximo' },
  { key: 'outstanding_risk_limit', label: 'Límite Riesgo Vivo' },
  { key: 'pending_integration_fund', label: 'Fondo Pendiente de Integrar' },
  { key: 'available_new_contributions', label: 'Disponible Aportes Nuevos' },
];

const formatMetricValue = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return '-';
  return amount.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const Sgr = () => {
  const fiscalId = useFiscalIdConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<SgrData>({
    logo: '',
    name: '',
    cuit: '',
    address: '',
    location: '',
    email: '',
    legalRepresentativeName: '',
    legalRepresentativeDni: '',
    legalRepresentativeCuit: '',
    legalRepresentativeEmail: '',
  });
  const [riskFundMetrics, setRiskFundMetrics] = useState<RiskFundMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  useEffect(() => {
    fetchSgrData();
    fetchRiskFundMetrics();
  }, []);

  const fetchRiskFundMetrics = async () => {
    setMetricsLoading(true);
    try {
      const response = await getRiskMetrics();
      if (response.success && 'data' in response) {
        setRiskFundMetrics(response.data as RiskFundMetrics);
      }
    } catch (error) {
      console.error('Error fetching risk fund metrics:', error);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchSgrData = async () => {
    setLoading(true);
    try {
      const response = await getSgrData();
      if (response.success && response.data) {
        setFormData(response.data);
      } else {
        useToast.error({
          title: response.message || 'Error al cargar los datos',
          id: 'load-error-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch (error) {
      useToast.error({
        title: 'Error al cargar los datos',
        id: 'load-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate(routes.settings);
  };

  const handleInputChange = (field: keyof SgrData) => (e: any) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleCuitChange = (e: any) => {
    setFormData({
      ...formData,
      cuit: cuitMask(e.target.value),
    });
  };

  const handleImageLoad = (_file: File, imageUrl: string) => {
    setFormData({
      ...formData,
      logo: imageUrl,
    });
  };

  const handleImageError = (error: string) => {
    useToast.error({
      title: error,
      id: 'image-error-toast',
      description: '',
      closeButton: true,
      autoClose: 5000,
    });
  };

  const handleSave = async () => {
    // Validaciones básicas
    if (!formData.name || !formData.email || !formData.address || !formData.location || !formData.cuit) {
      useToast.error({
        title: 'Por favor complete todos los campos obligatorios de la empresa',
        id: 'validation-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    // Validación de formato del CUIT de la empresa (11 dígitos + dígito verificador)
    const cuitError = validateCuit(formData.cuit);
    if (cuitError) {
      useToast.error({
        title: cuitError,
        id: 'validation-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    if (!formData.legalRepresentativeName || !formData.legalRepresentativeDni || 
        !formData.legalRepresentativeCuit || !formData.legalRepresentativeEmail) {
      useToast.error({
        title: 'Por favor complete todos los campos del representante legal',
        id: 'validation-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      return;
    }

    setSaving(true);
    try {
      const response = await updateSgrData(formData);
      if (response.success) {
        useToast.success({
          title: 'Datos guardados correctamente',
          id: 'save-success-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      } else {
        useToast.error({
          title: response.message || 'Error al guardar los datos',
          id: 'save-error-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
      }
    } catch (error) {
      useToast.error({
        title: 'Error al guardar los datos',
        id: 'save-error-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
        <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '3' }}>
          <Button 
            icon='arrow_left_outline'
            variant='tertiary'
            size='sm'
            iconAlign='center'
            onClick={handleGoBack}
            aria-label="Volver a configuración"
          />
          <H withMargin={false} type='1' weight='bold' modifier='strong'>
            Mis datos de empresa
          </H>
        </Card>
        <Card display='flex' justifyContent='center' alignItems='center' height='300px'>
          <Spinner size='lg' />
        </Card>
      </Card>
    );
  }

  return (
    <Card variant='neutral' modifier='inverted' spacing='4' height='-webkit-fill-available' width='85vw'>
      {/* Header */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button 
          icon='arrow_left_outline'
          variant='tertiary'
          size='sm'
          iconAlign='center'
          onClick={handleGoBack}
          aria-label="Volver a configuración"
        />
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Mis datos de empresa
        </H>
      </Card>

      {/* Primera Sección - Logo y datos de empresa */}
      <Card 
        spacing='4'
        margin={{ bottom: '3' }}
      >
        <div
          data-testid='sgr-company-layout'
          style={{
            display: 'grid',
            gridTemplateColumns: '200px minmax(0, 1fr)',
            gap: '89px',
            alignItems: 'start',
          }}
        >
          {/* Logo */}
          <div style={{ width: '200px', maxWidth: '100%', overflow: 'hidden' }}>
            <ImageUploader
              acceptedFormats={['.svg']}
              maxSizeKB={200}
              uploadLabel='Cargar tu logo'
              initialImage={formData.logo}
              onImageLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>

          {/* Datos de empresa — minWidth: 0 evita que la grilla interna desborde sobre el logo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
            {/* Fila 1 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '16px' }}>
              <Input
                label='Nombre'
                value={formData.name}
                onChange={handleInputChange('name')}
                placeholder='Nombre de la empresa'
                size='md'
              />
              <Input
                label='Email'
                type='email'
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder='email@empresa.com'
                size='md'
              />
              <Input
                id='sgr-cuit'
                label='CUIT de la empresa'
                value={cuitMask(formData.cuit || '')}
                onChange={handleCuitChange}
                placeholder='XX-XXXXXXXX-X'
                maxLength={13}
                size='md'
              />
            </div>

            {/* Fila 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
              <Input
                label='Dirección completa'
                value={formData.address}
                onChange={handleInputChange('address')}
                placeholder='Calle, Número, Piso, Depto'
                size='md'
              />
              <Input
                label='Locación'
                value={formData.location}
                onChange={handleInputChange('location')}
                placeholder='Ciudad, Provincia'
                size='md'
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Segunda Sección - Representante Legal */}
      <Card 
        spacing='4'
        margin={{ bottom: '3' }}
      >
        <Card margin={{ bottom: '3' }}>
          <H withMargin={false} type='2' weight='bold' modifier='strong'>
            Representante legal
          </H>
        </Card>

        {/* Fila de 4 inputs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px' }}>
          <Input
            label='Nombre y Apellido'
            required
            value={formData.legalRepresentativeName}
            onChange={handleInputChange('legalRepresentativeName')}
            placeholder='Juan Pérez'
            size='md'
          />
          <Input
            label={fiscalId.label}
            required
            value={formData.legalRepresentativeCuit}
            onChange={handleInputChange('legalRepresentativeCuit')}
            placeholder={fiscalId.country === 'UY' ? 'RUT o Cédula' : '20-12345678-9'}
            size='md'
          />
          <Input
            label={fiscalId.country === 'UY' ? 'Cédula' : 'DNI'}
            required
            value={formData.legalRepresentativeDni}
            onChange={handleInputChange('legalRepresentativeDni')}
            placeholder={fiscalId.country === 'UY' ? '1.234.567-8' : '12.345.678'}
            size='md'
          />
          <Input
            label='Email'
            type='email'
            required
            value={formData.legalRepresentativeEmail}
            onChange={handleInputChange('legalRepresentativeEmail')}
            placeholder='email@representante.com'
            size='md'
          />
        </div>
      </Card>

      {/* Botón Guardar */}
      <Card display='flex' justifyContent='flex-end' margin={{ bottom: '3' }}>
        <Button
          variant='primary'
          size='lg'
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </Card>

      {/* Tercera Sección - Estado del Fondo de Riesgo */}
      <Card spacing='4' margin={{ bottom: '3' }}>
        <Card margin={{ bottom: '3' }}>
          <H withMargin={false} type='2' weight='bold' modifier='strong'>
            Estado del fondo de riesgo
          </H>
        </Card>

        {metricsLoading ? (
          <Card display='flex' justifyContent='center' alignItems='center' height='200px'>
            <Spinner size='md' />
          </Card>
        ) : riskFundMetrics ? (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F5F7FA' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 'normal', fontSize: '14px', color: '#5A6B7B' }}>
                  Valor
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'normal', fontSize: '14px', color: '#5A6B7B' }}>
                  Resultado
                </th>
              </tr>
            </thead>
            <tbody>
              {RISK_FUND_ROWS.map((row) => (
                <tr key={row.key} style={{ borderBottom: '1px solid #DFE6EB' }}>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                    {row.label}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>
                    {formatMetricValue(riskFundMetrics[row.key])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <P variant='neutral' withMargin={false}>
            No se pudo cargar la información del fondo de riesgo
          </P>
        )}
      </Card>

      <Toast id='validation-error-toast' />
      <Toast id='save-success-toast' />
      <Toast id='save-error-toast' />
      <Toast id='load-error-toast' />
      <Toast id='image-error-toast' />
    </Card>
  );
};

export default Sgr;
